import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [lockTime, setLockTime] = useState(0);
  const { login, isLocked, getLockRemainingTime } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // 检查锁定状态
    const checkLock = () => {
      if (isLocked()) {
        setLockTime(getLockRemainingTime());
      } else {
        setLockTime(0);
      }
    };
    
    checkLock();
    const interval = setInterval(checkLock, 1000);
    return () => clearInterval(interval);
  }, [isLocked, getLockRemainingTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success) {
        navigate('/card-generation');
      } else {
        setError(result.error || 'Login failed');
        if (result.remainingAttempts !== undefined) {
          setRemainingAttempts(result.remainingAttempts);
        }
      }
    } catch {
      setError('Login failed, please try again');
    } finally {
      setLoading(false);
    }
  };

  const isFormDisabled = loading || lockTime > 0;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* 动态海洋背景视频 */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source
          src="https://cdn.pixabay.com/video/2020/05/25/40130-424930032_large.mp4"
          type="video/mp4"
        />
      </video>
      
      {/* 叠加层增强可读性 */}
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/40 via-blue-900/50 to-slate-900/70"></div>
      
      <div className="relative w-full max-w-md px-4 sm:px-6 z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/30 p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl mb-4 shadow-lg shadow-cyan-500/30">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wide drop-shadow-lg">
              Virtual Card Generator
            </h1>
            <p className="text-cyan-200/80 text-sm">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* 锁定提示 */}
            {lockTime > 0 && (
              <div className="bg-red-500/20 border border-red-400/50 rounded-lg p-3 sm:p-4 text-center backdrop-blur-sm">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-red-300 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-red-200 font-semibold">Account Locked</span>
                </div>
                <p className="text-red-200/80 text-sm">
                  Try again in <span className="font-mono font-bold">{lockTime}</span> minutes
                </p>
              </div>
            )}

            {error && !lockTime && (
              <div className="bg-red-500/20 border border-red-400/50 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-red-200 text-sm text-center">{error}</p>
                {remainingAttempts !== null && remainingAttempts > 0 && (
                  <p className="text-red-200/70 text-xs text-center mt-1">
                    {remainingAttempts} attempts remaining
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-cyan-100 mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-cyan-200/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all backdrop-blur-sm disabled:opacity-50"
                placeholder="Enter username"
                required
                autoComplete="username"
                disabled={isFormDisabled}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-cyan-100 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-cyan-200/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all backdrop-blur-sm disabled:opacity-50"
                placeholder="Enter password"
                required
                autoComplete="current-password"
                disabled={isFormDisabled}
              />
            </div>

            <button
              type="submit"
              disabled={isFormDisabled}
              className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : lockTime > 0 ? 'Account Locked' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-cyan-200/60">
            Authorized users only
          </div>
        </div>
      </div>
    </div>
  );
}
